import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Users, Clock, AlertCircle, CheckCircle, Archive, Eye } from 'lucide-react';
import api from '../utils/api';

export default function Threads() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    has_unread: false,
    needs_reply: false,
    archived: false
  });
  const [selectedThread, setSelectedThread] = useState(null);

  useEffect(() => {
    loadThreads();
  }, [filters]);

  async function loadThreads() {
    try {
      setLoading(true);
      const params = {};

      if (filters.needs_reply) {
        params.needs_reply = true;
      }
      if (filters.has_unread) {
        params.has_unread = true;
      }
      if (filters.archived) {
        params.archived = true;
      }

      const response = await api.get('/threads', { params });
      setThreads(response.data || []);
    } catch (err) {
      console.error('Failed to load threads:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkRead(threadId) {
    try {
      await api.post(`/threads/${threadId}/read`);
      await loadThreads();
      if (selectedThread?.id === threadId) {
        const response = await api.get(`/threads/${threadId}`);
        setSelectedThread(response.data);
      }
    } catch (err) {
      console.error('Failed to mark thread as read:', err);
    }
  }

  async function handleMarkNoReply(threadId) {
    try {
      await api.put(`/threads/${threadId}`, {
        needs_reply: false
      });
      await loadThreads();
      if (selectedThread?.id === threadId) {
        const response = await api.get(`/threads/${threadId}`);
        setSelectedThread(response.data);
      }
    } catch (err) {
      console.error('Failed to update thread:', err);
    }
  }

  async function handleArchiveThread(threadId) {
    try {
      await api.post(`/threads/${threadId}/archive`);
      loadThreads();
    } catch (err) {
      console.error('Failed to archive thread:', err);
    }
  }

  async function handleThreadClick(thread) {
    try {
      const response = await api.get(`/threads/${thread.id}`);
      setSelectedThread(response.data);
    } catch (err) {
      console.error('Failed to load thread details:', err);
    }
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading threads...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)]">
      {/* Thread list */}
      <div className="w-full lg:w-1/2 border-r border-gray-200 overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Conversations</h1>

          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.has_unread}
                onChange={(e) => setFilters(prev => ({ ...prev, has_unread: e.target.checked }))}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-sm">Unread only</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.needs_reply}
                onChange={(e) => setFilters(prev => ({ ...prev, needs_reply: e.target.checked }))}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-sm">Needs Reply</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.archived}
                onChange={(e) => setFilters(prev => ({ ...prev, archived: e.target.checked }))}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-sm">Archived</span>
            </label>
          </div>
        </div>

        {/* Thread list */}
        <div className="divide-y divide-gray-200">
          {threads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No threads found
            </div>
          ) : (
            threads.map((thread) => (
              <div
                key={thread.id}
                onClick={() => handleThreadClick(thread)}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedThread?.id === thread.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 flex-1 truncate">
                    {thread.subject}
                  </h3>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    {formatDate(thread.last_activity)}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {thread.email_count}
                  </span>

                  {thread.has_unread && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <AlertCircle className="w-4 h-4" />
                      Unread
                    </span>
                  )}

                  {thread.needs_reply && (
                    <span className="flex items-center gap-1 text-orange-600">
                      <Clock className="w-4 h-4" />
                      Needs Reply
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Thread detail */}
      <div className="w-1/2 overflow-y-auto bg-gray-50">
        {selectedThread ? (
          <div className="p-6">
            {/* Thread header */}
            <div className="mb-6 pb-6 border-b border-gray-200 bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {selectedThread.subject}
              </h2>

              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <Mail className="w-4 h-4" />
                {selectedThread.email_count} messages

                {selectedThread.has_unread && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="text-blue-600">Unread messages</span>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {selectedThread.has_unread && (
                  <button
                    onClick={() => handleMarkRead(selectedThread.id)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Mark Read
                  </button>
                )}

                {selectedThread.needs_reply && (
                  <button
                    onClick={() => handleMarkNoReply(selectedThread.id)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium flex items-center gap-1"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark No Reply Needed
                  </button>
                )}

                <button
                  onClick={() => handleArchiveThread(selectedThread.id)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium flex items-center gap-1"
                >
                  <Archive className="w-4 h-4" />
                  Archive Thread
                </button>
              </div>
            </div>

            {/* Emails in thread */}
            <div className="space-y-4">
              {selectedThread.emails?.map((email, index) => (
                <div
                  key={email.id}
                  className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 mb-1">
                        {email.from_address}
                      </div>
                      <div className="text-sm text-gray-600">
                        To: {email.to_address}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(email.date).toLocaleString()}
                    </div>
                  </div>

                  <div className="mt-2 text-sm text-gray-600">
                    {email.is_reply && (
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium mb-2">
                        Reply
                      </span>
                    )}
                    {email.is_sent && (
                      <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium mb-2 ml-1">
                        Sent
                      </span>
                    )}
                  </div>

                  <Link
                    to={`/emails?id=${email.id}`}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline mt-2 inline-block"
                  >
                    View full email →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Select a thread to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
